import React from 'react'
import { connect } from 'react-redux'
import { jt, t } from 'ttag'
import { Link } from 'react-router-dom'
import { Alert } from 'react-bootstrap'

import {
  closeBudgetHubConfirm,
  findBudgetAmountInRound,
  findBudgetChoicesInRound,
  loadBudget,
  saveBudgetToHub
} from '../../actions/assessments/budget'
import withRecharts from '../../components/withRecharts'
import LoadingIndicator from '../../components/LoadingIndicator'
import { fileDateSort, openFileWizardModal } from '../../actions/fileManagementWizard'
import { useInitDispatch, useTitleChange } from '../../util/hooks'

const BudgetSummaryScreen = props => {
  useTitleChange(t`${dynamicValues.budgetTitle} :: Summary`)
  useInitDispatch(loadBudget(null, false, true))

  const {
    onPopupFiles,
    closeSaveConfirm,
    onSave,
    showSave,
    main: { rounds, showSavedConfirm, result = {} }
  } = props
  const {
    monthlySpending = 0,
    annualTotal = 0,
    taxes = 0,
    occupation,
    reverse,
    responses,
    questions,
    startingSalary,
    monthlyTotal = 0
  } = result

  if (!result.questions) {
    return <LoadingIndicator newSiteLook={true} />
  }

  const { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } = props.recharts
  const options = {}

  questions.forEach(q => q.options.forEach(o => (options[o.id] = o.title)))

  const fmtCurrency = inp =>
    (inp || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  const spendData = [
    ...rounds.map(r => ({
      name: r.name(),
      value: (responses.find(r2 => r2.round == r.roundId) || {}).total
    })),
    { name: t`Taxes`, value: taxes.total / 12 }
  ]
    .filter(r => r.value > 0)
    .sort((a, b) => 0.5 - Math.random())

  if (reverse && monthlyTotal > 0) {
    spendData.push({ name: t`Unspent`, value: monthlyTotal })
  }

  console.log(rounds, spendData)

  const SpendChart = () => {
    return (
      <ResponsiveContainer aspect={6 / 3} width="100%">
        <PieChart width={500} height={200} isAnimationActive={false}>
          <Pie
            data={spendData}
            dataKey="value"
            label={p => {
              return (
                <text {...p} fill="black">
                  {p.payload.name} ({Math.ceil(100 * p.percent)}%)
                </text>
              )
            }}>
            {spendData.map((e, index) => (
              <Cell
                key={`cell${index}`}
                fill={dynamicValues.plotColors[index % dynamicValues.plotColors.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    )
  }
  const monthlySalary = <span className="bold">{fmtCurrency(startingSalary / 12)}</span>
  const monthlySpendingString = (
    <span className="bold">{fmtCurrency(monthlySpending + taxes.total / 12)}</span>
  )
  const monthlyRemainingString = (
    <span className={`bold ${monthlyTotal < 0 ? 'overspent overspent-callout' : ''}`}>
      {fmtCurrency(monthlyTotal)}
    </span>
  )
  const annualTotalString = <span className="bold">{fmtCurrency(annualTotal)}</span>
  const budgetOccString = occupation ? t`for ${occupation.title}` : ''
  const fedTaxStr = fmtCurrency(taxes.federal / 12)
  const stateTaxStr = fmtCurrency(taxes.state / 12)

  return (
    <div className="container budget" id="budget-summary">
      {showSavedConfirm.visible && (
        <Alert bsStyle="warning">
          <div className="report-alert-save-confirm">
            <a
              href="javascript:void(0)"
              className="pull-right"
              onClick={closeSaveConfirm}>
              <i className="fal fa-times"></i>
            </a>
            <h3>
              {(() => {
                const sharedFilesLink = (
                  <a
                    href="javascript:void(0)"
                    onClick={() => onPopupFiles({ id: result.userId })}>
                    {t`saved files`}
                  </a>
                )

                if (showSavedConfirm.forUser) {
                  return jt`This report has been saved to the file listing of ${showSavedConfirm.forUser}. You can access it in their ${sharedFilesLink}.`
                }

                return jt`This report has been saved to your Career Hub. You can access it in your ${sharedFilesLink}.`
              })()}
            </h3>
          </div>
        </Alert>
      )}
      <div id="actions">
        <div className="pull-right">
          <a className="btn" href="/pdfs/budget.pdf">
            <span style={{ fontSize: '1.2rem', color: 'rgb(102, 102, 102)' }}>
              <i className="print"></i> {t`Print`}
            </span>
          </a>
        </div>
        <div className="pull-right">
          {showSave && (
            <button className="btn btn-link" onClick={() => onSave(result.id)}>
              <span style={{ fontSize: '1.2rem', color: '#666' }}>
                <i className="save"></i> {t`Save to Career Hub`}{' '}
              </span>
            </button>
          )}
        </div>
      </div>
      <div>
        <p className="forward instructions clear">
          {!reverse &&
            jt`Based on your responses, you will need to have a job where you take home ${monthlySpendingString} a month,
                         or at least ${annualTotalString} a year when you include taxes.`}

          {reverse &&
            jt`You created a budget ${budgetOccString} where you take home ${monthlySalary} a month,
                        and budgeted ${monthlySpendingString} towards expenses. This will leave you ${monthlyRemainingString} a month.`}
        </p>

        <div className="toplevel noborder">
          <div className="earningslogo withicons"></div>

          {!reverse && (
            <div className="forward">
              {t`Monthly Spending:`} {monthlySpendingString}
            </div>
          )}

          {reverse && (
            <div className="reverse">
              <span>{jt`Monthly Earnings: ${monthlySalary} - Spending: ${monthlySpendingString} = Remaining: ${monthlyRemainingString}`}</span>
            </div>
          )}
        </div>

        <div className="detail-breakout row">
          <div className="col-sm-6">
            <div id="col1">
              <div className="withicons topcurve"></div>
            </div>
            <div id="col2">
              {rounds.map((r, idx) => (
                <Link
                  to={`/budget/round/${r.roundId}`}
                  key={r.id}
                  className={`section-container ${idx % 2 == 0 ? 'even' : 'odd'}`}
                  id={r.id}>
                  <div className={`withicons icon ${r.id}icon`}></div>
                  <div className="pull-left">
                    <div className="name">
                      <span>{r.name()}</span>
                    </div>
                    <div id={`${r.id}value`} className="selectedvalue">
                      {findBudgetChoicesInRound(r.roundId, responses)
                        .map(v => options[v])
                        .join(', ')}
                    </div>
                  </div>
                  <div id={`${r.id}valuecost`} className="selectedcost">
                    {findBudgetAmountInRound(r.roundId, responses)}
                  </div>
                  <br className="clear" />
                </Link>
              ))}

              <a id="taxes" className="section-container even">
                <div className="withicons icon taxesicon"></div>
                <div className="pull-left">
                  <div className="name">
                    <span>{t`Taxes`}</span>
                  </div>
                  <div id="taxesvalue" className="selectedvalue">
                    {t`Though only a rough estimate, without any deductions you will likely pay around ${fedTaxStr} in Federal Taxes and ${stateTaxStr} in State Taxes.`}
                  </div>
                </div>
                <div id="taxesvaluecost" className="selectedcost">
                  {fmtCurrency(taxes.total / 12)}
                </div>
                <br className="clear" />
              </a>

              {reverse && (
                <a className="section-container odd reverse">
                  <div className="withicons icon earningsicon"></div>
                  <div className="pull-left">
                    <div className="name">
                      <span>{t`Unspent`}</span>
                    </div>
                    <div className="selectedvalue"></div>
                  </div>
                  <div
                    className={`selectedcost  ${
                      monthlyTotal < 0 ? 'overspent overspent-callout' : ''
                    }`}>
                    <span className="mremain">{fmtCurrency(monthlyTotal)}</span>
                  </div>

                  <br className="clear" />
                </a>
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <p>{t`Below you can see how your budget choices relate to your overall budget.`}</p>
            <SpendChart />
            <br className="clear" />
            <div className="filter note-color" style={{ marginTop: '25px' }}>
              <h3>{t`Need to make changes or add items?`}</h3>
              <p>{t`You can select any of the sections in the summary to change selections.`}</p>
              <p>{t`Didn't see an option you expected? You can add it to your budget link by using the button below.`}</p>
              <div className="text-center">
                <Link className="btn no-style btn-primary" to="/budget/round/9">
                  {t`Add Other Items`}
                </Link>
              </div>
            </div>
          </div>
          <br className="clear" />
        </div>
        <div>
          <br className="clear" />
          <div className={`toplevel`}>
            <div className="earningslogo withicons"></div>
            <span>{t`Minimum Annual Salary:`}</span>{' '}
            <span className={`atotal`}>{fmtCurrency(annualTotal)}</span>
          </div>
          <br />
        </div>
        {(!reverse || (reverse && monthlyTotal < 0)) && (
          <div>
            {reverse && monthlyTotal < 0 && (
              <div className="alert alert-warning text-center">
                <h4
                  style={{
                    fontWeight: 'bold'
                  }}>
                  {t`Your budget exceeds the amount you expect to make.`}
                </h4>
                <p>{t`To explore alternate occupations which might fit your budget, click the button below.`}</p>
              </div>
            )}
            <div className="nav">
              {!reverse && (
                <p className="inst forward">{t`To find out which jobs might support your lifestyle, click the button below.`}</p>
              )}
              <form action="javascript://">
                <p className="actions overspent">
                  <Link to="/budget/results" id="summarylink" className="btn btn-success">
                    {t`View Occupations`}
                  </Link>
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default connect(
  state => {
    return state.assessments.budget
  },
  dispatch => ({
    onPopupFiles: user => {
      dispatch(openFileWizardModal(user, 'user', false)).then(() =>
        dispatch(fileDateSort())
      )
    },
    onSave: budget => {
      dispatch(saveBudgetToHub(budget))
    },
    closeSaveConfirm: () => {
      dispatch(closeBudgetHubConfirm())
    },
    dispatch
  })
)(withRecharts(BudgetSummaryScreen, LoadingIndicator))
